const siteKey = import.meta.env.VUE_APP_RECAPTCHA_KEY;

let loadPromise = null;

/**
 * Inject the reCAPTCHA v3 script on demand (idempotent).
 *
 * Once the script runs, Google keeps its badge in the DOM for the whole
 * session, so we only load it on the guest/auth pages that actually call
 * grecaptcha.execute (login / register / forgot / reset) rather than globally
 * at app startup.
 */
export function loadRecaptcha() {
  if (!siteKey) {
    return Promise.resolve(false);
  }

  if (!loadPromise) {
    loadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => {
        // Let a later attempt retry instead of caching the failure forever.
        loadPromise = null;
        reject(new Error('Failed to load reCAPTCHA'));
      };
      document.head.appendChild(script);
    });
  }

  return loadPromise;
}

/**
 * Run a reCAPTCHA v3 action and resolve with its token.
 *
 * Loads the script first when it is not on the page yet, so submitting a form
 * before the lazy load finished cannot leave `window.grecaptcha` undefined.
 */
export function executeRecaptcha(action) {
  // Already on the page (or stubbed in tests): skip the loader entirely.
  const ready = window.grecaptcha ? Promise.resolve(true) : loadRecaptcha();

  return ready.then((isLoaded) => {
    if (!isLoaded) {
      return '';
    }

    return new Promise((resolve, reject) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(siteKey, { action }).then(resolve).catch(reject);
      });
    });
  });
}
